import { useMemo } from 'react';
import {
  Box,
  Chip,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { Icon } from '@iconify/react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { VacancyScores } from 'types/vacancyStats';
import { formatCount, formatScore } from 'utils/vacancyStatsFormat';
import { CHART, EmptyState, KpiTile, Overline, WidgetCard } from './shared';

interface Props {
  scores: VacancyScores;
}

const ScoresWidget = ({ scores }: Props) => {
  const { evaluated, ofTotal, avg, median, min, max, distribution } = scores;

  const distributionSeries = useMemo(
    () => [{ name: 'Кандидаты', data: distribution.map((bucket) => bucket.count) }],
    [distribution]
  );

  const distributionOptions = useMemo<ApexOptions>(
    () => ({
      chart: { type: 'bar', fontFamily: 'inherit', toolbar: { show: false } },
      colors: [CHART.primary],
      plotOptions: { bar: { columnWidth: '10%', borderRadius: 4 } },
      dataLabels: { enabled: false },
      grid: { strokeDashArray: 3, borderColor: '#DFE3E8' },
      xaxis: {
        categories: distribution.map((bucket) => String(bucket.bucket)),
        title: { text: 'Балл', style: { fontSize: '12px', color: '#919EAB' } },
        axisBorder: { show: false },
        axisTicks: { show: false }
      },
      yaxis: {
        title: { text: 'Кол-во', style: { fontSize: '12px', color: '#919EAB' } },
        labels: { formatter: (val: number) => `${Math.round(val)}` }
      },
      tooltip: { y: { formatter: (val: number) => `${formatCount(val)} чел.` } }
    }),
    [distribution]
  );

  return (
    <WidgetCard overline="Средние оценки кандидатов" title="" coverage={{ evaluated, ofTotal }} sx={{ marginLeft: '0 !important' }}>
      {evaluated === 0 ? (
        <EmptyState />
      ) : (
        <Stack spacing={3}>
          {/* Крупные KPI: avg и median + вспомогательные min/max */}
          <Grid container spacing={2} sx={{ ml: '-12px !important' }}>
            <Grid item xs={6} sm={3}>
              <KpiTile label="Средний" value={formatScore(avg)} emphasis />
            </Grid>
            <Grid item xs={6} sm={3}>
              <KpiTile label="Медиана" value={formatScore(median)} emphasis />
            </Grid>
            <Grid item xs={6} sm={3}>
              <KpiTile label="Минимум" value={formatScore(min)} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <KpiTile label="Максимум" value={formatScore(max)} />
            </Grid>
          </Grid>

          {/* Гистограмма распределения по баллу */}
          {distribution.length > 0 && (
            <Box>
              <Overline>Распределение по баллу</Overline>
              <ReactApexChart
                options={distributionOptions}
                series={distributionSeries}
                type="bar"
                height={200}
              />
            </Box>
          )}
        </Stack>
      )}
    </WidgetCard>
  );
};

export default ScoresWidget;
